/*=========================================================================================
  File Name: main.js
  Description: main vue(js) file
  ----------------------------------------------------------------------------------------
  Item Name: Vuesax Admin - VueJS Dashboard Admin Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

import Vue from 'vue';
import App from './App.vue';

import * as Sentry from '@sentry/vue';

// Vuesax Component Framework
import Vuesax from 'vuesax';
import 'material-icons/iconfont/material-icons.css'; //Material Icons
import 'vuesax/dist/vuesax.css'; // Vuesax
Vue.use(Vuesax);

// Theme Configurations
import '../themeConfig.js';

// Globally Registered Components
import './globalComponents.js';

// Styles: SCSS
import './assets/scss/main.scss';

import VueI18n from 'vue-i18n';
Vue.use(VueI18n);

import ECharts from 'vue-echarts';
import 'echarts';

Vue.component('v-chart', ECharts);

const messages = {
  defaultLabels: {
    Consultor: 'Consultor',
    consultor: 'consultor',
    CONSULTOR: 'CONSULTOR',
  },
  default126: {
    Consultor: 'Corretor',
    consultor: 'corretor',
    CONSULTOR: 'CORRETOR',
  },
};

const i18n = new VueI18n({
  locale: 'defaultLabels',
  messages,
});

// Tailwind
import '@/assets/css/main.css';

// Vue Router
import router from './router';

/**
 * Initializes Sentry for error tracking, performance monitoring, and session replay in a Vue.js application.
 *
 * @param {object} options - Configuration options for Sentry initialization.
 * @param {Vue} options.Vue - The Vue instance to be integrated with Sentry.
 * @param {string} options.dsn - The Data Source Name (DSN) for Sentry. It should be retrieved from the environment variable VUE_APP_SENTRY_DSN.
 * @param {Array} options.integrations - An array of Sentry integrations to be applied during initialization.
 * @param {object} options.integrations[0] - Sentry BrowserTracing integration for distributed tracing.
 * @param {Array|string} options.integrations[0].tracePropagationTargets - Specifies the URLs for which distributed tracing should be enabled.
 * @param {VueRouter} options.integrations[0].routingInstrumentation - Sentry integration for Vue Router instrumentation.
 * @param {object} options.integrations[1] - Sentry Replay integration for session replay.
 * @param {number} options.tracesSampleRate - The sample rate for capturing transactions. Should be a value between 0.0 and 1.0.
 * @param {number} options.replaysSessionSampleRate - The sample rate for capturing sessions for replay. Should be a value between 0.0 and 1.0.
 * @param {number} options.replaysOnErrorSampleRate - The sample rate for capturing sessions where errors occur for replay. Should be a value between 0.0 and 1.0.
 */

if (process.env.VUE_APP_NODE_ENV === 'production') {
  Sentry.init({
    Vue,
    dsn: process.env.VUE_APP_SENTRY_DSN,
    integrations: [
      new Sentry.BrowserTracing({
        tracePropagationTargets: [/^https:\/\/bbonebrasil\.com.br/],
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      }),
      new Sentry.Replay(),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

// Vuex Store
import store from './store/store';

// Vuesax Admin Filters
import './filters/filters';

// Vuejs - Vue wrapper for hammerjs
import { VueHammer } from 'vue2-hammer';
Vue.use(VueHammer);

//import * as VueGoogleMaps from 'vue2-google-maps'
//Vue.use(VueGoogleMaps, {
// load: {
// Add your API key here
//  key: 'AIzaSyA2w0PY7eaKb3OeXu1v6gbhaN1JUKNAiJs',
// libraries: 'places', // This is required if you use the Auto complete plug-in
//},
//})

// PrismJS
import 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';

// FlatPickr (Datetime picker)
import 'flatpickr/dist/flatpickr.css';

// Feather font icon
require('./assets/css/iconfont.css');

import VeeValidate from 'vee-validate';
Vue.use(VeeValidate, {
  // This is the default
  inject: true,
  // Important to name this something other than 'fields'
  fieldsBagName: 'veeFields',
  // This is not required but avoids possible naming conflicts
  errorBagName: 'veeErrors',
});

import VueMask from 'v-mask';
Vue.use(VueMask);

import VueCurrencyFilter from 'vue-currency-filter';
Vue.use(VueCurrencyFilter, {
  symbol: 'R$',
  thousandsSeparator: '.',
  fractionCount: 2,
  fractionSeparator: ',',
  symbolPosition: 'front',
  symbolSpacing: true,
});

import VueSignaturePad from 'vue-signature-pad';

Vue.use(VueSignaturePad);

import VueTheMask from 'vue-the-mask';
Vue.use(VueTheMask);
import formatDate from './utils/pipe/formatDate';
Vue.use(formatDate);

var VueScrollTo = require('vue-scrollto');

Vue.use(VueScrollTo);

import { VTooltip, VPopover, VClosePopover } from 'v-tooltip';

Vue.directive('tooltip', VTooltip);
Vue.directive('close-popover', VClosePopover);
Vue.component('v-popover', VPopover);

import VueKonva from 'vue-konva';
Vue.use(VueKonva);

//#region Permissionamento
import { abilitiesPlugin } from '@casl/vue';
import ability from './services/ability';
Vue.use(abilitiesPlugin, ability.getPermission(), {
  useGlobalProperties: true,
});

Vue.prototype.$actionsDefault = ability.actionsDefault;

//#endregion

//#region Service Status Notification

// REMOVIDO TEMPORARIAMENTE

// import serviceStatusNotification from './services/statusNotification';
// serviceStatusNotification.start();

//#endregion

Vue.config.productionTip = false;

new Vue({
  router,
  store,
  i18n,
  render: (h) => h(App),
}).$mount('#app');
